import { useState, useEffect } from "react";
import { SANS, color, font, type, weight } from "./theme";
import { CloseButton, Icon, Screen } from "./ui";
import { authedFetch } from "./apiClient";
import { supabase } from "./supabase";
import { sanitizeShort, sanitizeMedium, sanitizeLong } from "./sanitize";

const SECTIONS = [
  { id: "analytics", label: "Analytics" },
  { id: "listing",   label: "Listing" },
  { id: "announce",  label: "Announcements" },
];

export default function PartnerDashboard({ user, placeId, onClose }) {
  const [section, setSection] = useState("analytics");
  const [venue, setVenue] = useState(null);
  // "idle" when there is no placeId to look up. Without this, a venue that is
  // simply not there is indistinguishable from one still loading, and the
  // header claims "Loading venue..." forever.
  const [venueState, setVenueState] = useState(placeId ? "loading" : "idle");

  useEffect(() => {
    if (!placeId) {
      setVenue(null);
      setVenueState("idle");
      return;
    }
    let cancelled = false;
    const loadVenue = async () => {
      setVenueState("loading");
      const { data, error } = await supabase
        .from("places")
        .select("*")
        .eq("place_id", placeId)
        .maybeSingle();
      if (cancelled) return;
      setVenue(data || null);
      // An RLS refusal or a network failure is not the same as "no such
      // venue", and the two need different advice.
      setVenueState(error ? "error" : data ? "loaded" : "missing");
    };
    loadVenue();
    return () => { cancelled = true; };
  }, [placeId]);

  const venueLabel =
    venueState === "loaded" ? (venue.name || "Unnamed venue")
    : venueState === "loading" ? "Loading venue..."
    : venueState === "missing" ? "Listing not saved yet"
    : venueState === "error" ? "Could not load venue"
    : "No venue linked";

  return (
    <Screen zIndex={500} maxWidth={900}>

      {/* The venue is what a partner cares about, not the words "partner
          dashboard". Slate, not gold: the accent tells you which surface you
          are on. */}
      <div style={{ background: color.bg, padding: "0 12px", height: 56, borderBottom: `1px solid ${color.border}`, display: "flex", alignItems: "center", gap: 12, position: "sticky", top: 0, zIndex: 10 }}>
        <Icon.Venue size={22} color={color.partner} />
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ fontFamily: font.display, fontSize: type.lg, fontWeight: weight.displayMed, color: color.textPrimary, lineHeight: 1.2, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
            {venueState === "loaded" ? venue.name || "Unnamed venue" : venueLabel}
          </div>
          {venueState === "loaded" && (
            <div style={{ fontSize: type.sm, color: color.textMuted }}>Partner dashboard · listing live</div>
          )}
        </div>
        <CloseButton onClose={onClose} />
      </div>

      {/* Section nav */}
      <div style={{ display: "flex", borderBottom: `1px solid ${color.line}`, background: color.bg, position: "sticky", top: 57, zIndex: 9 }}>
        {SECTIONS.map(s => (
          <button key={s.id} onClick={() => setSection(s.id)}
            style={{ flex: 1, height: 44, background: "none", border: "none", boxShadow: section === s.id ? `inset 0 -2px 0 ${color.partner}` : "none", color: section === s.id ? color.textPrimary : color.textFaint, fontSize: type.md, cursor: "pointer", fontFamily: font.sans }}>
            <span>{s.label}</span>
          </button>
        ))}
      </div>

      <div style={{ padding: 20 }}>
        {section === "analytics" && <AnalyticsSection placeId={placeId} venue={venue} />}
        {section === "listing"   && <ListingSection placeId={placeId} venue={venue} onVenueUpdate={setVenue} />}
        {section === "announce"  && <AnnounceSection placeId={placeId} user={user} />}
      </div>
    </Screen>
  );
}

// ─── ANALYTICS ───────────────────────────────────────────────────────────────

function AnalyticsSection({ placeId, venue }) {
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState(null);

  useEffect(() => {
    if (!placeId) return;
    loadAnalytics();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [placeId]);

  const loadAnalytics = async () => {
    setLoading(true);

    // Get all check-ins at this venue (matched by smoke_location containing venue name)
    const venueName = venue?.name || "";
    const { data: checkins } = await supabase
      .from("checkins")
      .select("id, user_id, created_at, cigar_id, cigar_name, cigar_brand, rating, cigars(brand, line)")
      .ilike("smoke_location", `%${venueName || placeId}%`)
      .order("created_at", { ascending: false });

    if (!checkins || checkins.length === 0) {
      setStats({ total: 0, unique: 0, topCigars: [], byDay: [], avgRating: null, repeatVisitors: 0 });
      setLoading(false);
      return;
    }

    const total = checkins.length;
    const uniqueUsers = new Set(checkins.map(c => c.user_id));
    const unique = uniqueUsers.size;

    // Repeat visitors (visited 2+ times)
    const visitCounts = {};
    for (const c of checkins) visitCounts[c.user_id] = (visitCounts[c.user_id] || 0) + 1;
    const repeatVisitors = Object.values(visitCounts).filter(v => v >= 2).length;

    // Average rating
    const rated = checkins.filter(c => c.rating != null);
    const avgRating = rated.length > 0
      ? parseFloat((rated.reduce((a, c) => a + c.rating, 0) / rated.length).toFixed(1))
      : null;

    // Top cigars
    const cigarCounts = {};
    for (const c of checkins) {
      const label = c.cigars ? `${c.cigars.brand} ${c.cigars.line}` : `${c.cigar_brand || ""} ${c.cigar_name || ""}`.trim();
      if (label) cigarCounts[label] = (cigarCounts[label] || 0) + 1;
    }
    const topCigars = Object.entries(cigarCounts).sort((a, b) => b[1] - a[1]).slice(0, 8).map(([label, count]) => ({ label, count }));

    // Check-ins by day of week
    const dayNames = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];
    const dayCounts = [0, 0, 0, 0, 0, 0, 0];
    for (const c of checkins) dayCounts[new Date(c.created_at).getDay()]++;
    const byDay = dayNames.map((name, i) => ({ name, count: dayCounts[i] }));

    setStats({ total, unique, repeatVisitors, avgRating, topCigars, byDay });
    setLoading(false);
  };

  if (loading) return <div style={{ textAlign: "center", padding: "40px 0", fontSize: 13, color: color.faint }}>Loading analytics...</div>;

  if (!stats || stats.total === 0) return (
    <div style={{ textAlign: "center", padding: "60px 20px" }}>
      <div style={{ marginBottom: 16, display: "flex", justifyContent: "center" }}><Icon.Feed size={36} color={color.borderStrong} /></div>
      <div style={{ fontSize: 15, fontWeight: 700, color: color.text, marginBottom: 8 }}>No check-ins yet</div>
      <div style={{ fontSize: 13, color: color.faint, lineHeight: 1.6 }}>When Ashed users check in at your venue, their activity will appear here.</div>
    </div>
  );

  const maxDay = Math.max(...stats.byDay.map(d => d.count), 1);
  const maxCigar = stats.topCigars[0]?.count || 1;

  return (
    <div>
      {/* Stat boxes */}
      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(110px, 1fr))", gap: 10, marginBottom: 24 }}>
        {[
          ["Total Visits", stats.total, color.partner],
          ["Unique Visitors", stats.unique, color.gold],
          ["Repeat Visitors", stats.repeatVisitors, color.green],
          ["Avg Rating", stats.avgRating ?? "—", color.alert],
        ].map(([label, value, accent]) => (
          <div key={label} style={{ background: color.surface, border: `1px solid ${accent}33`, borderRadius: 12, padding: "14px 12px", textAlign: "center", position: "relative", overflow: "hidden" }}>
            <div style={{ position: "absolute", top: 0, left: 0, right: 0, height: 3, background: accent, opacity: 0.6, borderRadius: "12px 12px 0 0" }} />
            <div style={{ fontSize: 24, fontWeight: 700, color: accent }}>{value}</div>
            <div style={{ fontSize: type.xs, color: "#6a5540", letterSpacing: 1, marginTop: 5 }}>{label.toUpperCase()}</div>
          </div>
        ))}
      </div>

      {/* Busiest days */}
      <div style={{ background: color.surface, border: `1px solid ${color.line}`, borderRadius: 12, padding: 16, marginBottom: 16 }}>
        <div style={{ fontSize: type.xs, color: color.cream, fontWeight: 600, marginBottom: 16 }}>Busiest Days</div>
        <div style={{ display: "flex", alignItems: "flex-end", gap: 6, height: 80 }}>
          {stats.byDay.map((d, i) => {
            const pct = Math.round((d.count / maxDay) * 100);
            const isZero = d.count === 0;
            return (
              <div key={i} style={{ flex: 1, display: "flex", flexDirection: "column", alignItems: "center", height: "100%", justifyContent: "flex-end", gap: 4 }}>
                {!isZero && <div style={{ fontSize: type.xs, color: color.partner, fontWeight: 700 }}>{d.count}</div>}
                <div style={{ width: "100%", borderRadius: "3px 3px 0 0", height: isZero ? 2 : `${Math.max(pct, 4)}%`, background: isZero ? color.surfaceRaised : color.partner, opacity: isZero ? 0.3 : 1 }} />
                <div style={{ fontSize: type.xs, color: color.dimAlt }}>{d.name}</div>
              </div>
            );
          })}
        </div>
      </div>

      {/* Top cigars */}
      <div style={{ background: color.surface, border: `1px solid ${color.line}`, borderRadius: 12, padding: 16 }}>
        <div style={{ fontSize: type.xs, color: color.cream, fontWeight: 600, marginBottom: 16 }}>Top Cigars Smoked Here</div>
        {stats.topCigars.map((c, i) => (
          <div key={i} style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 10 }}>
            <div style={{ fontSize: type.xs, color: color.faint, width: 16, textAlign: "right", flexShrink: 0 }}>{i + 1}</div>
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ fontSize: 13, color: color.text, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis", marginBottom: 4 }}>{c.label}</div>
              <div style={{ height: 5, background: color.surfaceRaised, borderRadius: 3, overflow: "hidden" }}>
                <div style={{ width: `${Math.round((c.count / maxCigar) * 100)}%`, height: "100%", background: color.partner, borderRadius: 3 }} />
              </div>
            </div>
            <div style={{ fontSize: 13, fontWeight: 700, color: color.partner, flexShrink: 0 }}>{c.count}</div>
          </div>
        ))}
      </div>
    </div>
  );
}

// ─── LISTING ─────────────────────────────────────────────────────────────────

// Declared at module scope deliberately. A component defined INSIDE another
// component gets a fresh identity on every render, so React treats it as a
// different type, unmounts the old subtree and mounts a new one. The <input>
// DOM node is destroyed and rebuilt on each keystroke, which is why typing a
// single character dropped focus and the field appeared to freeze.
function Field({ label, field, form, setForm, multiline }) {
  const box = { width: "100%", background: color.surfaceRaised, border: `1px solid ${color.lineInput}`, borderRadius: 8, padding: "10px 12px", color: color.text, fontSize: type.md, fontFamily: SANS, outline: "none", boxSizing: "border-box" };
  const onChange = e => setForm(p => ({ ...p, [field]: e.target.value }));
  return (
    <div style={{ marginBottom: 16 }}>
      <div style={{ fontSize: type.xs, color: color.muted, letterSpacing: 1, marginBottom: 6 }}>{label}</div>
      {multiline
        ? <textarea value={form[field]} onChange={onChange} rows={3} style={{ ...box, resize: "vertical" }} />
        : <input value={form[field]} onChange={onChange} style={box} />}
    </div>
  );
}

function ListingSection({ placeId, venue, onVenueUpdate }) {
  const [form, setForm] = useState({ name: "", address: "", phone: "", hours: "", description: "" });
  const [saving, setSaving] = useState(false);
  const [loadingGoogle, setLoadingGoogle] = useState(false);
  const [msg, setMsg] = useState(null);
  const [prefilled, setPrefilled] = useState(false);

  useEffect(() => {
    if (venue && (venue.address || venue.phone)) {
      // Already have saved data — use it
      setForm({
        name: venue.name || "",
        address: venue.address || "",
        phone: venue.phone || "",
        hours: venue.hours || "",
        description: venue.description || "",
      });
      setPrefilled(true);
    } else if (placeId && !prefilled) {
      // No saved data yet — fetch from Google and pre-populate
      fetchFromGoogle();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [venue, placeId]);

  const fetchFromGoogle = async () => {
    setLoadingGoogle(true);
    try {
      const res = await authedFetch(`/api/places?action=details&place_id=${encodeURIComponent(placeId)}`);
      const data = await res.json();
      const r = data.result;
      if (r) {
        const hoursText = r.opening_hours?.weekday_text?.join("\n") || "";
        setForm(prev => ({
          ...prev,
          name: r.name || prev.name,
          address: r.formatted_address || prev.address,
          phone: r.formatted_phone_number || prev.phone,
          hours: hoursText || prev.hours,
        }));
        setPrefilled(true);
        setMsg({ text: "Pre-filled from Google Maps — review and save to confirm.", isError: false });
        setTimeout(() => setMsg(null), 5000);
      }
    } catch (e) {
      console.error("Google prefill error:", e);
    }
    setLoadingGoogle(false);
  };

  const handleSave = async () => {
    setSaving(true);
    const { data, error } = await supabase
      .from("places")
      .upsert({ place_id: placeId, name: sanitizeShort(form.name), address: sanitizeMedium(form.address), phone: sanitizeShort(form.phone), hours: sanitizeMedium(form.hours), description: sanitizeLong(form.description) }, { onConflict: "place_id" })
      .select()
      .single();
    if (error) { setMsg({ text: "Error saving listing.", isError: true }); }
    else { onVenueUpdate(data); setMsg({ text: "Listing updated!", isError: false }); }
    setSaving(false);
    setTimeout(() => setMsg(null), 3000);
  };

  return (
    <div>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline", marginBottom: 20 }}>
        <div style={{ fontSize: type.xs, color: color.cream, fontWeight: 600 }}>Manage Your Listing</div>
        <button onClick={fetchFromGoogle} disabled={loadingGoogle}
          style={{ background: "none", border: `1px solid ${color.line}`, borderRadius: 20, padding: "4px 12px", color: color.muted, fontSize: type.xs, cursor: loadingGoogle ? "default" : "pointer", fontFamily: SANS }}>
          {loadingGoogle ? "Loading..." : "↻ Refresh from Google"}
        </button>
      </div>
      {msg && (
        <div style={{ background: msg.isError ? `${color.danger}22` : `${color.green}22`, border: `1px solid ${msg.isError ? `${color.danger}55` : `${color.green}55`}`, borderRadius: 8, padding: "10px 14px", marginBottom: 16, fontSize: 13, color: msg.isError ? color.dangerText : color.green, textAlign: "center" }}>
          {msg.text}
        </div>
      )}
      <Field label="VENUE NAME" field="name" form={form} setForm={setForm} />
      <Field label="ADDRESS" field="address" form={form} setForm={setForm} />
      <Field label="PHONE" field="phone" form={form} setForm={setForm} />
      <Field label="HOURS" field="hours" form={form} setForm={setForm} />
      <Field label="DESCRIPTION" field="description" form={form} setForm={setForm} multiline />
      <button onClick={handleSave} disabled={saving}
        style={{ width: "100%", background: saving ? color.line : color.partner, border: "none", borderRadius: 10, padding: 14, color: saving ? color.faint : color.text, fontSize: 14, fontWeight: 700, cursor: saving ? "default" : "pointer", fontFamily: SANS }}>
        {saving ? "Saving..." : "Save Listing"}
      </button>
    </div>
  );
}

// ─── ANNOUNCEMENTS ────────────────────────────────────────────────────────────

function AnnounceSection({ placeId, user }) {
  const [announcements, setAnnouncements] = useState([]);
  const [loading, setLoading] = useState(true);
  const [text, setText] = useState("");
  const [posting, setPosting] = useState(false);
  const [msg, setMsg] = useState(null);

  useEffect(() => { loadAnnouncements(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const loadAnnouncements = async () => {
    setLoading(true);
    const { data } = await supabase
      .from("announcements")
      .select("*")
      .eq("place_id", placeId)
      .order("created_at", { ascending: false });
    setAnnouncements(data || []);
    setLoading(false);
  };

  const handlePost = async () => {
    if (!text.trim()) return;
    setPosting(true);
    const { data, error } = await supabase
      .from("announcements")
      .insert({ place_id: placeId, user_id: user.id, content: sanitizeLong(text.trim()) })
      .select()
      .single();
    if (!error && data) {
      setAnnouncements(prev => [data, ...prev]);
      setText("");
    } else {
      setMsg("Error posting announcement.");
      setTimeout(() => setMsg(null), 3000);
    }
    setPosting(false);
  };

  const handleDelete = async (id) => {
    await supabase.from("announcements").delete().eq("id", id);
    setAnnouncements(prev => prev.filter(a => a.id !== id));
  };

  return (
    <div>
      <div style={{ fontSize: type.xs, color: color.cream, fontWeight: 600, marginBottom: 6 }}>Post Announcement</div>
      <div style={{ fontSize: type.xs, color: color.faint, marginBottom: 16 }}>Visible to users viewing your venue. Keep it short — events, new stock, specials.</div>

      {msg && <div style={{ background: `${color.danger}22`, border: `1px solid ${color.danger}55`, borderRadius: 8, padding: "10px 14px", marginBottom: 12, fontSize: 13, color: color.dangerText }}>{msg}</div>}

      <textarea
        value={text}
        onChange={e => setText(e.target.value)}
        placeholder="e.g. Herf night this Friday 7pm. New Padron 1964 shipment just arrived!"
        rows={3}
        style={{ width: "100%", background: color.surfaceRaised, border: `1px solid ${color.lineInput}`, borderRadius: 8, padding: "10px 12px", color: color.text, fontSize: type.md, fontFamily: SANS, outline: "none", boxSizing: "border-box", resize: "vertical", marginBottom: 10 }}
      />
      <button onClick={handlePost} disabled={posting || !text.trim()}
        style={{ width: "100%", background: text.trim() ? color.partner : color.surfaceRaised, border: "none", borderRadius: 10, padding: 12, color: text.trim() ? color.text : color.faint, fontSize: 13, fontWeight: 700, cursor: text.trim() ? "pointer" : "default", fontFamily: SANS, marginBottom: 24 }}>
        {posting ? "Posting..." : "Post announcement"}
      </button>

      <div style={{ fontSize: type.xs, color: color.muted, letterSpacing: 1, marginBottom: 12 }}>POSTED ANNOUNCEMENTS</div>
      {loading && <div style={{ fontSize: 13, color: color.faint, textAlign: "center", padding: "20px 0" }}>Loading...</div>}
      {!loading && announcements.length === 0 && (
        <div style={{ fontSize: 13, color: color.faint, textAlign: "center", padding: "30px 0" }}>No announcements posted yet.</div>
      )}
      {announcements.map(a => (
        <div key={a.id} style={{ background: color.surface, border: `1px solid ${color.line}`, borderRadius: 10, padding: 14, marginBottom: 10 }}>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 10 }}>
            <div style={{ fontSize: 13, color: color.text, lineHeight: 1.5, flex: 1 }}>{a.content}</div>
            <button onClick={() => handleDelete(a.id)}
              style={{ background: "none", border: "none", color: color.textFaint, fontSize: type.sm, cursor: "pointer", flexShrink: 0, minHeight: 44, minWidth: 44 }}>Delete</button>
          </div>
          <div style={{ fontSize: type.xs, color: color.faint, marginTop: 8 }}>
            {new Date(a.created_at).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" })}
          </div>
        </div>
      ))}
    </div>
  );
}